import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  AcademicCapIcon, 
  ChatBubbleLeftRightIcon,
  ShoppingBagIcon,
  BookOpenIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useChat } from '../contexts/ChatContext';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

interface MobileBottomNavProps {
  chatHistory?: any[];
  onSendMessage?: (message: string, type: 'question' | 'support' | 'learning', category: 'course_content' | 'assignment' | 'exam_prep' | 'technical' | 'general') => Promise<any>;
  isTyping?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const { openChat } = useChat();

  const navigation = [
    {
      name: 'Home',
      href: '/app',
      icon: HomeIcon,
      current: location.pathname === '/app'
    },
    {
      name: 'My Courses',
      href: '/app/courses',
      icon: AcademicCapIcon,
      current: location.pathname.startsWith('/app/courses')
    },
    {
      name: 'AI Chat',
      href: '#',
      icon: ChatBubbleLeftRightIcon,
      current: false,
      isAIChat: true
    },
    {
      name: 'Practice',
      href: '/app/practice',
      icon: BookOpenIcon,
      current: location.pathname === '/app/practice'
    },
    // Hide Shop on iOS WebView
    ...(shouldShowIOSWebViewAlert() ? [] : [
      {
        name: 'Shop',
        href: '/app/shop',
        icon: ShoppingBagIcon,
        current: location.pathname.startsWith('/app/shop')
      }
    ])
  ];

  return (
    <>
      {/* Beautiful Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
        {/* Background with glassmorphism effect */}
        <div className="absolute inset-0 bg-white/80 backdrop-blur-xl border-t border-gray-200/50 shadow-2xl" />
        
        {/* Main navigation container */}
        <div className="relative px-3 py-1">
          <div className="flex justify-around items-center">
            {navigation.map((item, index) => (
              <div key={item.name} className="flex flex-col items-center py-0.5 px-0.5 min-w-0 flex-1">
                {item.isAIChat ? (
                  // Enhanced AI Chat Button - Floating action button style
                  <button
                    onClick={() => openChat({ fullScreen: true })}
                    className="group relative flex flex-col items-center transition-all duration-300 ease-out"
                    title="AI Chat"
                  >
                    {/* Floating AI button with enhanced styling */}
                    <div className="relative -mt-6 mb-0.5">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-110 flex items-center justify-center border-4 border-white shadow-[0_8px_32px_rgba(0,0,0,0.12)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.16)] relative overflow-hidden group-hover:shadow-2xl">
                        {/* Animated background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        
                        {/* Icon with enhanced styling */}
                        <ChatBubbleLeftRightIcon className="h-8 w-8 text-white relative z-10 transition-transform duration-300 group-hover:scale-110" />
                        
                        {/* AI Status Indicator with pulse animation */}
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-indigo-400 to-indigo-500 rounded-full flex items-center justify-center shadow-lg border border-white animate-pulse">
                            <SparklesIcon className="h-2 w-2 text-white" />
                          </div>
                        
                        {/* Hover effect ring */}
                        <div className="absolute inset-0 rounded-full border-2 border-white/80 scale-0 group-hover:scale-100 transition-transform duration-300" />
                      </div>
                      
                      {/* Subtle glow effect */}
                      <div className="absolute inset-0 rounded-full bg-blue-400/20 blur-xl scale-0 group-hover:scale-100 transition-transform duration-500" />
                    </div>
                    
                    {/* Label with enhanced typography */}
                    <span className="text-[10px] font-semibold text-gray-700 group-hover:text-blue-600 transition-colors duration-300 truncate w-full text-center">
                      AI Chat
                    </span>
                  </button>
                ) : (
                  // Enhanced regular navigation items
                  <Link
                    to={item.href}
                    className={`group flex flex-col items-center transition-all duration-300 ease-out ${
                      item.current
                        ? 'text-blue-600'
                        : 'text-gray-600 hover:text-blue-500'
                    }`}
                  >
                    {/* Icon container with enhanced styling */}
                    <div className="relative mb-0.5">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                        item.current
                          ? 'bg-blue-50 border border-blue-200 shadow-sm'
                          : 'bg-gray-50/50 hover:bg-blue-50/80 border border-transparent hover:border-blue-200/50'
                      }`}>
                        <item.icon className={`h-6 w-6 transition-all duration-300 ${
                          item.current 
                            ? 'text-blue-600 scale-110' 
                            : 'text-gray-500 group-hover:text-blue-500 group-hover:scale-105'
                        }`} />
                      </div>
                      
                      {/* Active indicator dot */}
                      {item.current && (
                        <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full animate-pulse" />
                      )}
                      
                      {/* Hover effect ring */}
                      <div className={`absolute inset-0 rounded-lg border scale-0 transition-transform duration-300 ${
                        item.current
                          ? 'border-blue-300/50 scale-100'
                          : 'border-blue-200/30 group-hover:scale-100'
                      }`} />
                    </div>
                    
                    {/* Enhanced label styling */}
                    <span className={`text-[10px] font-medium transition-all duration-300 truncate w-full text-center ${
                      item.current 
                        ? 'text-blue-600 font-semibold' 
                        : 'text-gray-600 group-hover:text-blue-500'
                    }`}>
                      {item.name}
                    </span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom safe area for devices with home indicators */}
        <div className="h-0.5 bg-gradient-to-t from-gray-100/50 to-transparent" />
      </div>

    </>
  );
};

export default MobileBottomNav;
